import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './ItemDetails.css';
import { supabase } from '../config/supabase';

const ItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedSpice, setSelectedSpice] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [relatedItems, setRelatedItems] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('restaurantCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });

  // Fetch item details
  useEffect(() => {
    fetchItemDetails();
  }, [id]);

  // Fetch related items
  useEffect(() => {
    if (item) {
      fetchRelatedItems();
    }
  }, [item]);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      try {
        const savedCart = localStorage.getItem('restaurantCart');
        setCart(savedCart ? JSON.parse(savedCart) : []);
      } catch {
        setCart([]);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const fetchItemDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        // Extract customizations from JSONB field
        const customizations = data.customizations || {};
        const sizes = customizations.sizes || [];
        const spiceLevels = customizations.spiceLevels || [];
        const addons = customizations.addons || [];

        // Transform addons to ensure they have IDs (use index if no ID)
        const transformedAddons = addons.map((addon, index) => ({
          id: addon.id || `addon-${index}`,
          name: addon.name || '',
          description: addon.description || '',
          price: parseFloat(addon.price) || 0,
          icon: addon.icon || '➕'
        }));

        // Transform sizes to ensure prices are numbers
        const transformedSizes = sizes.map(size => ({
          name: size.name || '',
          price: parseFloat(size.price) || 0
        }));

        const transformed = {
          id: data.id,
          name: data.name,
          description: data.description || '',
          category: data.category || 'Other',
          price: parseFloat(data.price) || 0,
          originalPrice: data.original_price ? parseFloat(data.original_price) : null,
          discountPercentage: data.discount_percentage || 0,
          imageUrl: data.image_url || data.images_urls?.[0] || '',
          images: data.images_urls || [data.image_url].filter(Boolean),
          rating: parseFloat(data.rating) || 0,
          reviewsCount: data.reviews_count || 0,
          isVegetarian: data.is_vegetarian || false,
          isVegan: data.is_vegan || false,
          tags: data.tags || [],
          isFeatured: data.is_featured || false,
          isPopular: data.is_popular || false,
          isNew: data.is_new || false,
          isBestseller: data.is_bestseller || false,
          preparationTime: data.preparation_time || data.cooking_time || 0,
          calories: data.calories || null,
          sizes: transformedSizes,
          spiceLevels: spiceLevels,
          addons: transformedAddons
        };
        setItem(transformed);
        if (transformedSizes && transformedSizes.length > 0) {
          setSelectedSize(transformedSizes[0].name);
        }
        if (spiceLevels && spiceLevels.length > 0) {
          setSelectedSpice(spiceLevels[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchRelatedItems = async () => {
    try {
      if (!item) return;
      
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('category', item.category)
        .neq('id', item.id)
        .eq('is_available', true)
        .limit(4);

      if (error) throw error;

      const transformed = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        price: parseFloat(item.price) || 0,
        imageUrl: item.image_url || item.images_urls?.[0] || '',
        rating: parseFloat(item.rating) || 0
      }));

      setRelatedItems(transformed);
    } catch (error) {
      console.error('Error fetching related items:', error);
    }
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const calculateTotal = () => {
    if (!item) return 0;
    
    let basePrice = item.price;
    
    // Use size price if selected (replace base price, not add to it)
    if (selectedSize && item.sizes && item.sizes.length > 0) {
      const size = item.sizes.find(s => s.name === selectedSize);
      if (size && size.price) {
        basePrice = parseFloat(size.price || 0);
      }
    }
    
    // Add addon prices
    const addonsTotal = item.addons
      .filter(addon => selectedAddons.includes(addon.id))
      .reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0);
    
    return (basePrice + addonsTotal) * quantity;
  };

  const handleAddToCart = () => {
    if (!item) return;

    // Calculate the final price with customizations
    const finalPrice = calculateTotal() / quantity; // Price per item

    // Get full addon objects for selected addons
    const selectedAddonObjects = item.addons.filter(addon => selectedAddons.includes(addon.id));

    // Create cart item with all details
    const cartItem = {
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      price: finalPrice,
      imageUrl: item.images && item.images.length > 0 ? item.images[0] : '',
      quantity: quantity,
      selectedSize: selectedSize,
      selectedSpice: selectedSpice,
      selectedAddons: selectedAddonObjects, // Store full addon objects
      specialInstructions: specialInstructions,
      finalPrice: calculateTotal() // Store total price for the entire quantity
    };

    // Get current cart from localStorage
    const currentCart = JSON.parse(localStorage.getItem('restaurantCart') || '[]');
    
    // Check if item already exists in cart (same item with same customizations)
    const existingItemIndex = currentCart.findIndex(cartItem => 
      cartItem.id === item.id &&
      cartItem.selectedSize === selectedSize &&
      cartItem.selectedSpice === selectedSpice &&
      JSON.stringify(cartItem.selectedAddons?.map(a => a.id).sort()) === JSON.stringify(selectedAddons.sort()) &&
      cartItem.specialInstructions === specialInstructions
    );

    let updatedCart;
    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      updatedCart = currentCart.map((item, index) => 
        index === existingItemIndex
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      // Add new item to cart
      updatedCart = [...currentCart, cartItem];
    }

    // Save to localStorage
    localStorage.setItem('restaurantCart', JSON.stringify(updatedCart));
    
    // Update local state
    setCart(updatedCart);
    
    // Dispatch event to notify other components
    window.dispatchEvent(new Event('cartUpdated'));

    // Show success notification
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 2000);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  if (loading) {
    return (
      <div className="item-details-page">
        <CustomerHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <div className="item-details-loading">
          <div className="item-details-loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="item-details-page">
        <CustomerHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <div className="item-details-error">
          <p>Item not found</p>
          <button onClick={() => navigate('/')}>Go Back</button>
        </div>
      </div>
    );
  }

  const currentImage = item.images && item.images.length > 0 
    ? item.images[selectedImage] 
    : item.imageUrl;

  return (
    <div className="item-details-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Back Navigation */}
      <div className="item-details-back-nav">
        <div className="item-details-back-container">
          <button 
            className="item-details-back-btn"
            onClick={() => navigate(-1)}
          >
            <span className="item-details-back-icon">←</span>
            <span className="item-details-back-text">Back to Menu</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="item-details-main">
        <div className="item-details-content">
          {/* LEFT COLUMN - Image Gallery */}
          <div className="item-details-gallery">
            <div className="item-details-image-main">
              {currentImage ? (
                <img
                  src={currentImage}
                  alt={item.name}
                  className="item-details-image"
                />
              ) : (
                <div className="item-details-image-placeholder">🍽️</div>
              )}

              {/* Badges */}
              <div className="item-details-badges">
                {item.isPopular && (
                  <span className="item-details-badge item-details-badge-popular">
                    Popular
                  </span>
                )}
                {item.isNew && (
                  <span className="item-details-badge item-details-badge-new">
                    New
                  </span>
                )}
                {item.isBestseller && (
                  <span className="item-details-badge item-details-badge-bestseller">
                    Best Seller
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {item.images && item.images.length > 1 && (
              <div className="item-details-thumbnails">
                {item.images.map((img, index) => (
                  <div
                    key={index}
                    className={`item-details-thumbnail ${selectedImage === index ? 'item-details-thumbnail-active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <img
                      src={img}
                      alt={`${item.name} ${index + 1}`}
                      className="item-details-thumbnail-img"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Item Details */}
          <div className="item-details-info">
            {/* Header Section */}
            <div className="item-details-header">
              <div className="item-details-category">
                <span className="item-details-category-icon">
                  {item.category === 'Chicken' ? '🍗' : 
                   item.category === 'Pizza' ? '🍕' :
                   item.category === 'Burgers' ? '🍔' :
                   item.category === 'Chinese' ? '🥡' :
                   item.category === 'Drinks' ? '🥤' :
                   item.category === 'Desserts' ? '🍰' : '🍽️'}
                </span>
                {item.category}
              </div>

              <h1 className="item-details-title">{item.name}</h1>


              {/* Meta Info */}
              <div className="item-details-meta">
                {item.preparationTime > 0 && (
                  <div className="item-details-meta-item">
                    <span className="item-details-meta-icon">⏱️</span>
                    <span>{item.preparationTime} min</span>
                  </div>
                )}
                {item.calories && (
                  <div className="item-details-meta-item">
                    <span className="item-details-meta-icon">🔥</span>
                    <span>{item.calories} cal</span>
                  </div>
                )}
              </div>

              {/* Price Section */}
              <div className="item-details-price-section">
                <span className="item-details-price">
                  Rs. {calculateTotal().toFixed(2)}
                </span>
                {item.originalPrice && item.originalPrice > item.price && (
                  <>
                    <span className="item-details-price-original">
                      Rs. {(item.originalPrice * quantity).toFixed(2)}
                    </span>
                    {item.discountPercentage > 0 && (
                      <span className="item-details-discount">
                        {item.discountPercentage}% off
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Description */}
            {item.description && (
              <div className="item-details-description">
                <div className="item-details-description-label">Description</div>
                <p className="item-details-description-text">
                  {item.description}
                </p>
              </div>
            )}

            {/* Customization Section */}
            <div className="item-details-customization">
              <h2 className="item-details-customization-heading">Customize Your Order</h2>

              {/* Size Selection */}
              {item.sizes && item.sizes.length > 0 && (
                <>
                  <div className="item-details-size-label">Select Size</div>
                  <div className="item-details-size-options">
                    {item.sizes.map((size, index) => (
                      <div
                        key={index}
                        className={`item-details-size-option ${selectedSize === size.name ? 'item-details-size-option-active' : ''}`}
                        onClick={() => setSelectedSize(size.name)}
                      >
                        <span className="item-details-size-name">{size.name}</span>
                        <span className="item-details-size-price">
                          {parseFloat(size.price || 0) > 0 ? `Rs. ${parseFloat(size.price).toFixed(2)}` : 'Rs. 0.00'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Spice Level */}
              {item.spiceLevels && item.spiceLevels.length > 0 && (
                <>
                  <div className="item-details-spice-label">Spice Level</div>
                  <div className="item-details-spice-options">
                    {item.spiceLevels.map((level, index) => (
                      <div
                        key={index}
                        className={`item-details-spice-option ${selectedSpice === level ? 'item-details-spice-option-active' : ''}`}
                        onClick={() => setSelectedSpice(level)}
                      >
                        <span className="item-details-spice-icon">
                          {level === 'Spicy' ? '🌶️' : level === 'Medium' ? '🌶' : '🌿'}
                        </span>
                        <span className="item-details-spice-text">{level}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Add-ons */}
              {item.addons && item.addons.length > 0 && (
                <>
                  <div className="item-details-addons-label">Add-ons</div>
                  <div className="item-details-addons-list">
                    {item.addons.map((addon) => (
                      <div
                        key={addon.id}
                        className={`item-details-addon ${selectedAddons.includes(addon.id) ? 'item-details-addon-active' : ''}`}
                        onClick={() => handleAddonToggle(addon.id)}
                      >
                        <div className="item-details-addon-left">
                          <div className={`item-details-checkbox ${selectedAddons.includes(addon.id) ? 'item-details-checkbox-checked' : ''}`}>
                            {selectedAddons.includes(addon.id) && (
                              <span className="item-details-checkmark">✓</span>
                            )}
                          </div>
                          <span className="item-details-addon-icon">{addon.icon || '➕'}</span>
                          <div className="item-details-addon-details">
                            <span className="item-details-addon-name">{addon.name}</span>
                            {addon.description && (
                              <span className="item-details-addon-description">
                                {addon.description}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="item-details-addon-price">
                          +Rs. {addon.price.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Special Instructions */}
              <div className="item-details-instructions-label">Special Instructions (Optional)</div>
              <textarea
                className="item-details-textarea"
                placeholder="Any special requests or dietary requirements..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                maxLength={200}
              />
              <div className="item-details-char-counter">
                {specialInstructions.length}/200
              </div>
            </div>

            {/* Add to Cart Section */}
            <div className="item-details-cart-section">
              <div className="item-details-quantity-row">
                <span className="item-details-quantity-label">Quantity</span>
                <div className="item-details-quantity-controls">
                  <button
                    className="item-details-qty-btn item-details-qty-minus"
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                  >
                    −
                  </button>
                  <span className="item-details-quantity-display">{quantity}</span>
                  <button
                    className="item-details-qty-btn item-details-qty-plus"
                    onClick={increaseQuantity}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="item-details-total-row">
                <span className="item-details-total-label">Total</span>
                <span className="item-details-total-amount">
                  Rs. {calculateTotal().toFixed(2)}
                </span>
              </div>

              <button
                className="item-details-add-btn"
                onClick={handleAddToCart}
              >
                <span className="item-details-add-icon">🛒</span>
                <span>Add to Cart</span>
              </button>
            </div>
          </div>
        </div>

        {/* Related Items */}
        {relatedItems.length > 0 && (
          <div className="item-details-related">
            <h2 className="item-details-related-heading">You May Also Like</h2>
            <div className="item-details-related-grid">
              {relatedItems.map((relatedItem) => (
                <div
                  key={relatedItem.id}
                  className="item-details-related-card"
                  onClick={() => navigate(`/item/${relatedItem.id}`)}
                >
                  {relatedItem.imageUrl ? (
                    <img
                      src={relatedItem.imageUrl}
                      alt={relatedItem.name}
                      className="item-details-related-image"
                    />
                  ) : (
                    <div className="item-details-related-image">🍽️</div>
                  )}
                  <div className="item-details-related-name">{relatedItem.name}</div>
                  <div className="item-details-related-price">
                    Rs. {relatedItem.price.toFixed(2)}
                  </div>
                  <button className="item-details-related-btn">View Details</button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Success Notification */}
      {showNotification && (
        <div className="item-details-notification">
          <span className="item-details-notification-icon">✓</span>
          <span className="item-details-notification-text">Item added to cart!</span>
        </div>
      )}

      {/* Floating Cart Button */}
      <button
        className="item-details-floating-cart-button"
        onClick={() => navigate('/cart')}
        aria-label="Open cart"
      >
        <span className="item-details-floating-cart-icon">🛒</span>
        {getCartItemCount() > 0 && (
          <span className="item-details-floating-cart-badge">{getCartItemCount()}</span>
        )}
      </button>
    </div>
  );
};

export default ItemDetails;

